import { Button } from "@progress/kendo-react-buttons";
import { Checkbox } from "@progress/kendo-react-inputs";
import { useEffect, useMemo, useState } from "react";
import { useLazyListSettingsQuery, useSaveSettingsMutation } from "../../api/settingsApi";
import { useAppDispatch } from "../../app/hooks";
import { notify } from "../../components/notify";
import { SettingKeys, type SettingDto } from "../../contracts";

const AdminSettings: React.FC = () => {
    const dispatch = useAppDispatch();
    const [loadSettings, { data, isLoading, isError }] = useLazyListSettingsQuery();
    const [saveSettings, { isLoading: isSaving }] = useSaveSettingsMutation();

    const [editedValues, setEditedValues] = useState<Record<string, string>>({});

    useEffect(() => {
        void loadSettings({});
    }, [loadSettings]);

    const settingsByKey = useMemo((): Record<string, SettingDto> => {
        const items: SettingDto[] = data?.items ?? [];
        return Object.fromEntries(items.map(o => [o.key, o]));
    }, [data]);

    const getValue = (key: string): string | undefined => {
        return editedValues[key] ?? settingsByKey[key]?.value ?? undefined;
    };

    const setValue = (key: string, value: string): void => {
        setEditedValues(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const hasChanges: boolean = Object.keys(editedValues).some((key: string) => {
        return editedValues[key] !== (settingsByKey[key]?.value ?? undefined);
    });

    const handleSaveSettings = async (): Promise<void> => {
        const settingsToSave: SettingDto[] = Object.keys(editedValues)
            .map((key: string): SettingDto | undefined => {
                const existing: SettingDto | undefined = settingsByKey[key];
                if (!existing) {
                    return undefined;
                }

                return {
                    id: existing.id,
                    key: existing.key,
                    user_id: existing.user_id,
                    value: editedValues[key]
                };
            })
            .filter((o: SettingDto | undefined): o is SettingDto => o !== undefined);

        if (!settingsToSave.length) {
            dispatch(notify("There are no changes to save.", "warning"));
            return;
        }

        try {
            await saveSettings({ items: settingsToSave }).unwrap();
            await loadSettings({}).unwrap();
            setEditedValues({});
            dispatch(notify("Settings were saved successfully.", "success"));
        } catch {
            dispatch(notify("Unable to save settings right now.", "error"));
        }
    };

    return (
        <div className="p-3">
            <div className="d-flex flex-column gap-3">
                <section>
                    <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                            <h3 className="h6 mb-1">Admin Settings</h3>
                            <p className="text-muted mb-0">Configure administrative-level platform features.</p>
                        </div>
                        <Button
                            themeColor="primary"
                            onClick={handleSaveSettings}
                            disabled={!hasChanges || isLoading || isSaving}
                        >
                            {isSaving ? "Saving..." : "Save Settings"}
                        </Button>
                    </div>

                    {isError && (
                        <div className="alert alert-danger mb-3" role="alert">
                            Unable to load admin settings right now.
                        </div>
                    )}

                    <div className="p-3 border rounded-3 bg-light-subtle">
                        <Checkbox
                            label="Is Fake AI Agent Enabled"
                            checked={getValue(SettingKeys.is_fake_ai_agent_enabled)?.toLowerCase() === "true"}
                            disabled={isLoading}
                            onChange={event => setValue(
                                SettingKeys.is_fake_ai_agent_enabled,
                                event.value ? "true" : "false"
                            )}
                        />
                    </div>
                </section>
            </div>
        </div>
    );
};

export default AdminSettings;
